import { AgGridWidget, createContextMenu } from '@fortem-monorepo/react-ag'
import type { AccruedInterests } from '@fortem-monorepo/web-sdk'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { useUserContext } from '@/index'
import { useDailyAccruedInterests } from '@/react-query/queries/accrued-interests'

import { useAccruedInterestActions } from '../AccruedInterestActions.context'
import { useAccruedInterestsContext } from '../AccruedInterests.context'
import { aggregateDailyRows } from '../daily-group'
import { centeredColumn, dailyColumns } from './columns'

export const DailyAccruedTab = () => {
  const { params } = useAccruedInterestsContext()
  const query = useDailyAccruedInterests(params)
  const actions = useAccruedInterestActions()
  const { hasPermissions } = useUserContext()
  const canWrite = hasPermissions((permissions) => ({ permissions: [permissions.WRITE_OPERATIONS] }))
  const menu = createContextMenu<AccruedInterests.DailyRow>((event) => {
    const row = event.node?.data
    const defaults = event.defaultItems ?? []
    if (!row || event.node?.group || !canWrite) return defaults
    return [
      {
        name: 'Correct daily accrued interest',
        disabled: query.isFetching || !!actions.actionState,
        action: () => actions.openDailyCorrection({ params: { ...params }, row: { ...row } }),
      },
      'separator',
      ...defaults,
    ]
  })
  const guardedQuery = {
    ...query,
    refetch: async () => {
      if (!params.fund_name || actions.actionState) return query
      return query.refetch()
    },
  }
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {!params.fund_name && (
        <p className="shrink-0 p-3 text-sm text-muted-foreground">
          Select a fund and date to load daily accrued interests.
        </p>
      )}
      {query.isError && (
        <p role="alert" className="shrink-0 p-3 text-sm text-destructive">
          Unable to load daily accrued interests. {getErrorMessage(query.error)}
        </p>
      )}
      <AgGridWidget
        query={guardedQuery}
        widget={{ exposed: true, className: 'min-h-0 flex-1' }}
        widgetHeader={{ title: 'Daily accrued interests', className: 'shrink-0' }}
        widgetContent={{ className: 'min-h-0 flex-1 overflow-hidden' }}
        grid={{
          rowData: query.data ?? [],
          columnDefs: dailyColumns,
          defaultColDef: centeredColumn,
          autoGroupColumnDef: {
            headerName: 'Instrument',
            minWidth: 260,
            pinned: 'left',
            cellStyle: { textAlign: 'center' },
            headerClass: 'header-center',
          },
          groupDefaultExpanded: 0,
          getGroupRowAgg: ({ nodes }) =>
            aggregateDailyRows(
              nodes.flatMap((node) => {
                const row = node.group ? node.aggData : node.data
                return row ? [row] : []
              }),
            ),
          suppressAggFuncInHeader: true,
          domLayout: 'normal',
          getContextMenuItems: menu,
          getRowId: ({ data }: { data: AccruedInterests.DailyRow }) => `${data.trade_id}:${data.value_date}`,
        }}
      />
    </div>
  )
}
